import { Directive, OnDestroy } from "@angular/core";
import { GridApi } from "ag-grid-community";
import { Subject, Subscription, isObservable } from "rxjs";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { ValidationRuleController } from "~app/common/validation/ValidationRuleController";
import { RaskColumnButtonTypes } from "../../models/rask-column";
import { RaskColumnParam } from "../../models/rask-column-param";
import { RaskDataChanged } from "../../models/rask-data-changed";

@Directive()

export class GridBaseComponent implements OnDestroy {

    api: GridApi;
    actualCellWidth: string;

    get value(): any {
        return this.privateValue;
    }

    set value(val: any) {
        if (val === this.privateValue) {
            return;
        }
        this.privateValue = val;
        this.valueChangedCallback(val);
    }

    text: string;
    onClick: (v) => void;
    name: string;
    values: any;
    valuesFunction: any;
    placeholder: string;
    row: any;
    dataChangedSubscription: Subscription;
    rule: ValidationRuleController;
    readonly: boolean;
    disabled: boolean;
    tooltipValue?: string;
    buttonType?: (RaskColumnButtonTypes | ((item: any) => RaskColumnButtonTypes));

    viewFormatter?: any;
    formatter?: any;
    comparePredicate?: any;
    multiselectorBindLabel: string;
    multiselectorBindValue: string;
    componentOptions: any;

    protected readonly destroy$ = new Subject<void>();
    private privateValue: any;

    valueChangedCallback = (val) => {
        // Basklass-metod
    };

    agInit(params: RaskColumnParam): void {
        this.componentOptions = params.column?.componentOptions ? params.column?.componentOptions(params.model) : null;
        this.setPrivateValue(params);

        this.valueChangedCallback = params.valueChangedCallback;
        if (params.column.placeholder) {
            if (typeof (params.column.placeholder) === "function") {
                this.placeholder = params.column.placeholder(params.model) != null ? params.column.placeholder(params.model) : "";
            } else {
                this.placeholder = params.column.placeholder;
            }
        } else {
            this.placeholder = "";
        }
        this.disabled = params.column.disabled;
        this.row = params.model;
        this.name = params.column.field;
        this.text = params.column.text;
        this.rule = params.column.rule;
        this.onClick = params.column.onClick;
        this.initValues(params.column.values);
        this.setReadOnly(params);
        this.buttonType = params.column.buttonType;
        this.viewFormatter = params.column.viewFormatter;
        this.formatter = params.column.formatter;
        this.comparePredicate = params.column.comparePredicate;

        this.multiselectorBindLabel = params.column.multiselectorBindLabel;
        this.multiselectorBindValue = params.column.multiselectorBindValue;

        this.dataChangedSubscription = params.dataChanged.subscribe(m => this.handleDataChanged(m));
        this.api = params.api;
        if (params.column.actualWidth > 0) {
            this.actualCellWidth = `${params.column.actualWidth}px !important`;
        }
        this.onInit();
    }

    onInit() {
        // Metod för initiering. Överrids från basklasser om man behöver initiera i tidigt skede (agInit-skede)
    }

    getTooltipValue(tooltipGenerator: (r, v) => string, row: any, value: any) {
        return tooltipGenerator ? tooltipGenerator(row, value) : undefined;
    }

    handleDataChanged(e: RaskDataChanged) {
        if (!ObjectUtils.hasValue(this.valuesFunction) || e.model !== this.row || e.field === this.name) {
            return;
        }
        this.refreshValues();
    }

    refreshValues() {
        const newValues = this.valuesFunction(this.row);

        if (ObjectUtils.isNullOrUndefined(newValues)) {
            this.values = [];
        } else if (isObservable(newValues)) {
            this.values = newValues;
        } else {
            this.values = this.clone(newValues);
        }
    }

    initValues(values: any) {
        if (!ObjectUtils.hasValue(values)) {
            return;
        }

        if (typeof (values) === "function") {
            this.valuesFunction = values;
            this.refreshValues();
            return;
        }

        if (isObservable(values)) {
            this.values = values;
        } else {
            this.values = this.clone(values);
        }
    }

    clone(obj: any) {
        return JSON.parse(JSON.stringify(obj));
    }

    ngOnDestroy() {
        this.dataChangedSubscription.unsubscribe();
        this.destroy$.next();
        this.destroy$.complete();
    }

    private setPrivateValue(params: RaskColumnParam) {
        if (params.value !== null && Array.isArray(params.value)) {
            this.privateValue = params.value;
            this.tooltipValue = params.tooltipText || this.getTooltipValue(params.column.tooltipGenerator, params.model, this.privateValue);
        } else if ((typeof (params.value) === "object" && params.value !== null) && params.value.text === undefined) {
            this.privateValue = params.value;
            this.tooltipValue = params.value.tooltipText || this.getTooltipValue(params.column.tooltipGenerator, params.model, this.privateValue);
        } else if (typeof (params.value) === "object" && params.value !== null) {
            this.privateValue = params.value.text;
            this.tooltipValue = params.value.tooltipText || this.getTooltipValue(params.column.tooltipGenerator, params.model, this.privateValue);
        } else {
            this.tooltipValue = params.tooltipText || this.getTooltipValue(params.column.tooltipGenerator, params.model, this.privateValue);
            this.privateValue = params.value;
        }
    }

    private setReadOnly(params: RaskColumnParam) {
        if (params.column.readonly) {
            if (typeof (params.column.readonly) === "function") {
                this.readonly = params.column.readonly(params.model);
            } else {
                this.readonly = params.column.readonly;
            }
        }
    }

}
